import { cloneElement, forwardRef, ReactElement } from 'react';
import { Button, ButtonLink } from '@zoopla-eng/zds-components/button';
import { Box } from '@zoopla-eng/zds-components/box';
import { Divider } from '@zoopla-eng/zds-components/divider';
import {
    actionContainerStyle,
    successContainerStyle,
    successMessageStyle
} from './YourSituationQuestion.styles.css';
import { fontGroups } from '@zoopla-eng/zds-components/themes';

type ResultProps = {
    goal?: string;
    lastQuestionId?: string;
    actionElement: ReactElement;
    successMessage?: string;
};

export const Result = forwardRef<HTMLDivElement, ResultProps>(
    ({ goal, lastQuestionId, actionElement, successMessage }, ref) => {
        const actionElementWithProps = cloneElement(actionElement, {
            goal,
            lastQuestionId
        });

        return (
            <>
                <Box py="M100">
                    <Divider direction="horizontal" />
                </Box>
                <div className={successContainerStyle} ref={ref} tabIndex={-1}>
                    <Box pb="S500">
                        <p
                            className={`${successMessageStyle} ${fontGroups.ladle}`}
                            data-testid="your-situation-success-message"
                        >
                            {successMessage ||
                                "Thanks for sharing with us. We'll show more of what you need and less of what you don't."}
                        </p>
                    </Box>
                    <div className={actionContainerStyle}>
                        {actionElementWithProps}
                    </div>
                </div>
            </>
        );
    }
);

type ResultLinkProps = {
    goal?: string;
    lastQuestionId?: string;
    href: string;
    onClick: (goal?: string, lastQuestionId?: string) => void;
};

const YourSituationResultLink = ({
    goal,
    lastQuestionId,
    href,
    onClick
}: ResultLinkProps) => {
    const getLink = () => {
        if (href !== '/my-zoopla/') {
            return href;
        }

        switch (goal) {
            case 'rent_a_property':
                return '/to-rent/';
            case 'buy_a_property':
                return '/for-sale/';
            case 'track_the_value_of_my_home':
            case 'sell_my_property':
                return '/my-home/';
            default:
                return '/my-zoopla/';
        }
    };

    return (
        <ButtonLink
            data-testid="your-situation-question-close"
            category="primary"
            fw
            href={getLink()}
            onClick={() => onClick(goal, lastQuestionId)}
        >
            Continue
        </ButtonLink>
    );
};

type ResultButtonProps = {
    goal?: string;
    lastQuestionId?: string;
    onClick: (goal?: string, lastQuestionId?: string) => void;
};

const YourSituationResultButton = ({
    goal,
    lastQuestionId,
    onClick
}: ResultButtonProps) => (
    <Button
        data-testid="your-situation-question-close"
        category="primary"
        fw
        onClick={() => onClick(goal, lastQuestionId)}
    >
        Close
    </Button>
);

export const YourSituationResult = {
    Link: YourSituationResultLink,
    Button: YourSituationResultButton
};

// we export the button as default to use dynamic importing in the modal
export default YourSituationResultButton;
